import React, { useEffect, useState } from 'react';
import { useTheme } from "@mui/material";
import { tokens } from "../../../theme";
import { Heading, Text } from "../../../components/DesignSystem/components/Typography";
import { Button as DSButton } from "../../../components/DesignSystem/components/Button";
import { ArrowLeft, ArrowRight, X, Heart, Search, Baby, Users, CheckCircle } from "lucide-react";
import { useNavigate } from "react-router-dom";
import * as masterConstantActions from "../../../actions/masterConstant"
import * as userAction from "../../../actions/user"
import { Spinner } from "react-bootstrap";
import { useRef } from "react";
import imgFamily1 from "../../../assets/images/r-bg.png";
import imgFamily2 from "../../../assets/images/st2.png";

export const RelationshipFamily = () => {
    const navigate = useNavigate();
    
    // 1. Theme Configuration Hook
    const theme = useTheme();
    const mode = theme.palette.mode;
    
    // 2. FOUC Tracker State
    const [isTailwindLoaded, setIsTailwindLoaded] = useState(!!window.tailwind);

    const [selectedHaveKids, setSelectedHaveKids] = useState(null);
    const [selectedWantKids, setSelectedWantKids] = useState(null);
    const [errorMessagehaveKids, setErrorMessagehaveKids] = useState("");
    const [errorMessageWantKids, setErrorMessageWantKids] = useState("");
    const [loading, setLoading] = useState(false);
    const [familyOptions, setFamilyOptions] = useState([]);
    const [kidsOptions, setKidsOptions] = useState([]);
    const isFetched = useRef(false);

    const fetchWantKids = async () => {
        try {
            const response = await masterConstantActions.getConstantWantKids();
            if (response.status && response.data.length > 0) {
                setFamilyOptions(response.data);
            }
        } catch (error) {
            console.error("Error fetching want kids options:", error);
        }
    };

    const fetchHaveKids = async () => {
        try {
            const response = await masterConstantActions.getConstantHaveKids();
            if (response.status && response.data.length > 0) {
                setKidsOptions(response.data);
            }
        } catch (error) {
            console.error("Error fetching have kids options:", error);
        }
    };

    const fetchSelectedOptions = async () => {
        try {
            const response = await userAction.getKidsFamily();
            if (response) {
                setSelectedHaveKids(kidsOptions[response.have_kid_id - 1]);
                setSelectedWantKids(familyOptions[response.want_kid_id - 1]);
            }
        } catch (error) {
            console.error("Error fetching selected kids option:", error);
        }
    };

    useEffect(() => {
        if (isFetched.current) return;
        isFetched.current = true;

        fetchWantKids();
        fetchHaveKids();
    }, []);

    useEffect(() => {
        if (kidsOptions?.length > 0 || familyOptions?.length > 0) {
            fetchSelectedOptions();
        }
    }, [kidsOptions, familyOptions]);

    async function saveHaveKids() {
        if (!selectedHaveKids) {
            setErrorMessagehaveKids("*Please make a selection.");
            return;
        }
        let body = JSON.stringify({ have_kid_id: kidsOptions.indexOf(selectedHaveKids) + 1 });
        const response = await userAction.UpdateKidsFamily(body);
        if (!response) {
            throw new Error('Error saving have kids');
        }
    }

    async function saveWantKids() {
        if (!selectedWantKids) {
            setErrorMessageWantKids("*Please make a selection.");
            return;
        }
        let body = JSON.stringify({ want_kid_id: familyOptions.indexOf(selectedWantKids) + 1 });
        const response = await userAction.UpdateWantKids(body);
        if (!response) {
            throw new Error('Error saving want kids');
        }
    }

    async function saveAll() {
        setErrorMessagehaveKids("");
        setErrorMessageWantKids("");
        if (!selectedHaveKids) {
            setErrorMessagehaveKids("*Please make a selection.");
            return;
        }
        if (!selectedWantKids) {
            setErrorMessageWantKids("*Please make a selection.");
            return;
        }
        setLoading(true);
        navigate('/lifestyle');
        
        try {
            Promise.all([saveHaveKids(), saveWantKids()]).catch((error) => {
                console.error('Error saving kids and family in background:', error);
            });
        } catch (error) {
            console.error('Error executing update logic:', error);
        } finally {
            setLoading(false);
        }
    }

    // 3. Centralized FOUC Prevention & Engine Mount
    useEffect(() => {
        const applyConfig = () => {
            const modeColors = tokens(mode);
            if (window.tailwind) {
                window.tailwind.config = {
                    darkMode: "class",
                    theme: {
                        extend: {
                            colors: {
                                "brand": modeColors.greenAccent[500] || "#ff794d",
                                "background-light": mode === "dark" ? modeColors.primary[500] : "#f8f6f5",
                                "background-dark": modeColors.primary[500] || "#23140f",
                                "action-black": "#111111",
                            },
                            fontFamily: {
                                "display": ["Inter", "sans-serif"],
                                "body": ['"Source Sans Pro"', "sans-serif"],
                            },
                        },
                    },
                };
            }
        };

        if (window.tailwind) {
            applyConfig();
            setIsTailwindLoaded(true);
        } else {
            const script = document.createElement("script");
            script.src = "https://cdn.tailwindcss.com?plugins=forms";
            script.onload = () => {
                applyConfig();
                setIsTailwindLoaded(true);
            };
            document.head.appendChild(script);
        }
    }, [mode]);

    return (
        <div 
            className={`${mode === "dark" ? "dark" : ""} bg-background-light dark:bg-background-dark font-display text-slate-900 dark:text-slate-100 antialiased h-screen overflow-hidden w-full`}
            style={{ visibility: isTailwindLoaded ? 'visible' : 'hidden', opacity: isTailwindLoaded ? 1 : 0, transition: 'opacity 0.2s ease-in-out' }}
        >
            <div className="flex flex-col lg:flex-row min-h-screen">
                
                {/* ---------- LEFT SIDE: VISUAL PANEL ---------- */}
                <div className="relative hidden lg:flex lg:w-1/2 bg-brand/10 overflow-hidden items-center justify-center p-12 h-screen z-10 w-full">
                    <div className="absolute inset-0 opacity-20">
                        <div className="absolute top-0 left-0 w-full h-full bg-[radial-gradient(circle_at_center,_var(--tw-gradient-stops))] from-brand via-transparent to-transparent"></div>
                    </div>
                    <div className="relative z-10 max-w-md text-center flex flex-col items-center justify-center">
                        <div className="mb-8 flex justify-center">
                            <div className="w-16 h-16 rounded-full bg-brand flex items-center justify-center text-white shadow-xl shadow-primary/30">
                                <Heart className="w-8 h-8 fill-white" />
                            </div>
                        </div>
                        <Heading variant="h1" className="text-4xl lg:text-5xl 2xl:text-6xl font-black tracking-tight mb-6 text-slate-900 dark:text-slate-100">
                            Intentional <br />Connections.
                        </Heading>
                        <Text as="p" className="text-base lg:text-lg text-slate-600 dark:text-slate-400 leading-relaxed mb-8">
                            We believe in being upfront about what matters. Sharing your family goals helps us find matches who truly align with your vision for the future.
                        </Text>
                        
                        <div className="mt-8 grid grid-cols-2 gap-4 w-full">
                            <div className="aspect-square rounded-2xl bg-slate-200 dark:bg-slate-800 flex items-center justify-center overflow-hidden border border-brand/20">
                                <img className="object-cover w-full h-full opacity-80" alt="A happy couple walking together in a park" src={imgFamily1} />
                            </div>
                            <div className="aspect-square rounded-2xl bg-slate-200 dark:bg-slate-800 flex items-center justify-center overflow-hidden translate-y-8 border border-brand/20">
                                <img className="object-cover w-full h-full opacity-80" alt="A family playing together in a backyard" src={imgFamily2} />
                            </div>
                        </div>
                    </div>
                </div>

                {/* ---------- RIGHT SIDE: FORM PANEL ---------- */}
                <div className="w-full lg:w-1/2 flex flex-col bg-white dark:bg-background-dark h-screen overflow-y-auto custom-scrollbar">
                    
                    {/* Header/Nav */}
                    <header className="sticky top-0 z-30 bg-background-light/90 dark:bg-background-dark/90 backdrop-blur-md px-[var(--ds-space-6)] py-[var(--ds-space-4)] flex items-center justify-between border-b border-brand/10">
                        <button type="button" onClick={() => navigate(-1)} className="flex items-center justify-center h-10 w-10 shrink-0 rounded-full bg-brand/10 text-action-black dark:text-white hover:bg-brand/20 transition-colors" aria-label="Go back">
                            <ArrowLeft className="shrink-0" size={24} />
                        </button>
                        <div className="flex items-center gap-[var(--ds-space-2)]">
                            <div className="h-2 w-24 bg-brand/20 rounded-full overflow-hidden">
                                <div className="h-full bg-brand" style={{ width: '67%' }}></div>
                            </div>
                            <span className="text-xs font-bold uppercase tracking-wider text-brand">Step 4 of 6</span>
                        </div>
                        <button type="button" className="flex items-center justify-center h-10 w-10 shrink-0 rounded-full bg-brand/10 text-action-black dark:text-white hover:bg-brand/20 transition-colors" aria-label="Close">
                            <X className="shrink-0" size={24} />
                        </button>
                    </header>

                    <main className="flex-1 px-[var(--ds-space-6)] py-[var(--ds-space-6)] lg:px-[var(--ds-space-12)] lg:py-[var(--ds-space-12)] max-w-2xl mx-auto w-full flex flex-col">
                        <div className="mb-[var(--ds-space-8)]">
                            <Heading variant="h1" className="ds-heading-page-title text-slate-900 dark:text-slate-100 mb-[var(--ds-space-2)]">
                                Relationship & Family
                            </Heading>
                            <Text as="p" className="ds-text-page-subtitle text-slate-500 dark:text-slate-400">
                                Help us understand what you're looking for. Be as honest as possible. This information is key to our matching algorithm.
                            </Text>
                        </div>

                        <div className="space-y-10 pb-10 flex-1">                          
                            {/* Question 1: Do you have children? */}
                            <section>
                                <Heading variant="h4" className="text-lg font-bold flex items-center gap-2 mb-4 text-slate-900 dark:text-slate-100">
                                    <Baby className="w-5 h-5 text-brand" />
                                    What about kids?
                                </Heading>
                                <div className="flex flex-wrap gap-3">
                                    {kidsOptions?.map((kid, index) => (
                                        <label key={index} className="cursor-pointer" onClick={() => setSelectedHaveKids(kid)}>
                                            <input 
                                                className="hidden peer" 
                                                name="have_kids" 
                                                type="radio" 
                                                checked={selectedHaveKids?.id === kid?.id}
                                                readOnly
                                            />
                                            <div className="px-6 py-3 rounded-full border-2 border-slate-200 dark:border-slate-800/80 peer-checked:border-brand peer-checked:bg-brand peer-checked:text-white dark:text-slate-300 text-slate-600 font-medium transition-all hover:border-brand/50">
                                                {kid?.name}
                                            </div>
                                        </label>
                                    ))}
                                </div>
                                {errorMessagehaveKids && <p className="text-red-500 text-xs mt-2">{errorMessagehaveKids}</p>}
                            </section>

                            {/* Question 2: Thoughts on future children? */}
                            <section>
                                <Heading variant="h4" className="text-lg font-bold flex items-center gap-2 mb-4 text-slate-900 dark:text-slate-100">
                                    <Users className="w-5 h-5 text-brand" />
                                    What about family plans?
                                </Heading>
                                <div className="grid grid-cols-1 gap-3">
                                    {familyOptions?.map((family, index) => (
                                        <label key={index} className="relative flex items-center p-4 cursor-pointer rounded-xl border-2 border-slate-200 dark:border-slate-800/80 hover:border-brand/50 transition-all has-[:checked]:border-brand has-[:checked]:bg-brand/5 group" onClick={() => setSelectedWantKids(family)}>
                                            <input 
                                                className="hidden peer" 
                                                name="want_kids" 
                                                type="radio" 
                                                checked={selectedWantKids?.id === family?.id}
                                                readOnly
                                            />
                                            <div className="flex flex-col">
                                                <span className="font-bold text-slate-900 dark:text-slate-100">{family?.name}</span>
                                            </div>
                                            <CheckCircle className="ml-auto text-brand opacity-0 peer-checked:opacity-100 w-6 h-6 transition-opacity" />
                                        </label>
                                    ))}
                                </div>
                                {errorMessageWantKids && <p className="text-red-500 text-xs mt-2">{errorMessageWantKids}</p>}
                            </section>
                        </div>
                        
                        {/* Action Button - Sticky Bottom for Mobile */}
                        <div className="sticky bottom-0 -mx-[var(--ds-space-6)] px-[var(--ds-space-6)] py-4 lg:mx-0 lg:px-0 lg:py-0 lg:static bg-background-light dark:bg-background-dark/90 lg:bg-transparent lg:dark:bg-transparent backdrop-blur-md lg:backdrop-blur-none z-20 mt-[var(--ds-space-6)] lg:mt-0 lg:pt-[var(--ds-space-2)] border-t border-slate-200 dark:border-slate-800 lg:border-none">
                            <div className="flex gap-4 w-full">
                                <DSButton 
                                    type="button"
                                    variant="secondary"
                                    className="flex-1"
                                    onClick={() => navigate('/lifestyle')}
                                    disabled={loading}
                                >
                                    Ask me later
                                </DSButton>
                                <DSButton 
                                    type="button"
                                    variant="cta"
                                    className="flex-1"
                                    onClick={saveAll}
                                    disabled={loading}
                                >
                                    {loading ? <Spinner animation="border" size="sm" /> : "Continue"}
                                </DSButton>
                            </div>
                            <Text as="p" className="text-center text-slate-400 dark:text-slate-500 text-xs mt-4 lg:mt-6 font-medium px-4">
                                You can always update these preferences in your settings later.
                            </Text>
                        </div>
                    </main>
                </div>
            </div>
            {/* Inject minimal scrollbar styles for this component */}
            <style dangerouslySetInnerHTML={{__html: `
                .custom-scrollbar::-webkit-scrollbar { width: 4px; }
                .custom-scrollbar::-webkit-scrollbar-track { background: transparent; }
                .custom-scrollbar::-webkit-scrollbar-thumb { background: #ff794d33; border-radius: 10px; }
            `}} />
        </div>
    );
};
